import {
  ReceiptNature,
  ElectionDesignation,
  InKindNature,
  IN_KIND_NATURE_OPTIONS,
  FederalTransferQualification,
  PaymentMethod,
  paymentMethodLabel,
} from '@signalvote/shared';
import { TransferQualificationField } from './TransferQualificationField';
import type { ElectionOption } from '@/hooks/useElectionDesignationOptions';

export interface ContributionFormFieldsProps {
  amount: string;
  onAmountChange: (value: string) => void;

  date: string;
  onDateChange: (value: string) => void;

  contributionType: ReceiptNature;
  onContributionTypeChange: (value: ReceiptNature) => void;
  typeOptions: { value: ReceiptNature; label: string }[];
  showMoreTypesButton: boolean;
  onShowMoreTypes: () => void;


  inKindFairMarketValueBasis: string;
  onInKindFmvChange: (value: string) => void;

  /**
   * Treasurer-supplied text naming WHAT was given (e.g. "Catering"). Required
   * for every in-kind contribution — the DB rejects an in-kind row without it,
   * and it lands verbatim on the filing. Empty string when not yet entered.
   */
  inKindDescription: string;
  onInKindDescriptionChange: (value: string) => void;

  /**
   * AFU-002: the chosen AL in-kind nature, or null when not yet picked. The
   * selector renders only for in-kind; AL in-kind requires it before save
   * (gated by the parent's canSave). Reuses the existing 8-value enum.
   */
  inKindNature: InKindNature | null;
  onInKindNatureChange: (value: InKindNature | null) => void;
  /** AFU-002: true only for AL in-kind — drives the required asterisk so a
   *  non-AL filer isn't shown a false mandatory marker. */
  inKindNatureRequired: boolean;
  /**
   * #711 — whether the treasurer has confirmed a federal transfer came from another committee
   * authorized by the same candidate, the relationship that earns the limit exemption. Only read
   * when the selected receipt is a federal transfer; the control renders only there.
   */
  transferQualification: FederalTransferQualification | null;
  onTransferQualificationChange: (q: FederalTransferQualification) => void;

  /**
   * How a monetary gift was paid, or null until the treasurer answers. Asked on every receipt but
   * in-kind: the cash cap keys on currency, which the Cash receipt TYPE does not say.
   */
  paymentMethod: PaymentMethod | null;
  onPaymentMethodChange: (value: PaymentMethod | null) => void;

  /**
   * True only for Alabama campaigns. The date-of-receipt "earlier-of" rule
   * (Ala. Code §17-5-2(a)(3)(c)) is AL-specific; Federal/GA/LA/VA differ, so a
   * non-AL treasurer following it could record a gift into the wrong reporting
   * period. Gates the AL statute note vs. a jurisdiction-neutral one.
   */
  showAlabamaReceiptDateRule: boolean;

  /**
   * #144 — the shared election options (own cycle + next cycle on federal; today's plain list on
   * AL/VA), from `useElectionDesignationOptions`. The select binds to each option's composite
   * `key` so the own-cycle option (null year) stays distinct from the next-cycle option.
   */
  electionOptions: ElectionOption[];
  selectedElectionKey: string;
  onElectionChange: (designation: ElectionDesignation, electionYear: number | null) => void;

  notes: string;
  onNotesChange: (value: string) => void;
}

// In-kind is a receipt type, not a way money changes hands, so it is never a monetary answer.
const MONETARY_PAYMENT_METHODS = Object.values(PaymentMethod).filter((pm) => pm !== PaymentMethod.InKind);

export function ContributionFormFields({
  amount,
  onAmountChange,
  date,
  onDateChange,
  contributionType,
  onContributionTypeChange,
  typeOptions,
  showMoreTypesButton,
  onShowMoreTypes,
  inKindFairMarketValueBasis,
  onInKindFmvChange,
  inKindDescription,
  onInKindDescriptionChange,
  inKindNature,
  onInKindNatureChange,
  inKindNatureRequired,
  transferQualification,
  onTransferQualificationChange,
  paymentMethod,
  onPaymentMethodChange,
  showAlabamaReceiptDateRule,
  electionOptions,
  selectedElectionKey,
  onElectionChange,
  notes,
  onNotesChange,
}: ContributionFormFieldsProps) {
  return (
    <>
      <div>
        <label htmlFor="contrib-amount" className="block text-sm font-medium mb-1">Amount *</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
          <input
            id="contrib-amount"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value.replace(/[^0-9.]/g, ''))}
            onBlur={() => {
              const num = parseFloat(amount);
              if (!isNaN(num)) onAmountChange(num.toFixed(2));
            }}
            className="w-full rounded-md border pl-7 pr-3 py-2 text-sm"
            placeholder="0.00"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contrib-date" className="block text-sm font-medium mb-1">Date received *</label>
        <input
          id="contrib-date"
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm"
        />
        {/* §17-5-2(a)(3)(c) "earlier-of" rule is Alabama-specific — we don't derive
            this date, the treasurer owns it. A wrong date misfiles both the period and
            the CY aggregate. Other jurisdictions define receipt differently, so a non-AL
            treasurer sees only the neutral reminder that the date drives the period. */}
        {showAlabamaReceiptDateRule ? (
          <p className="mt-1 text-xs text-muted-foreground">
            For a check, use the earlier of the deposit date or 10 days after it came
            into the campaign&apos;s control (Ala. Code §17-5-2(a)(3)(c)). This date
            sets which report and which calendar-year total the contribution counts
            toward.
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">
            The day the money reached the campaign — not the date written on a check. This
            date determines which reporting period the contribution falls in.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contrib-type" className="block text-sm font-medium mb-1">Contribution Type</label>
        <select
          id="contrib-type"
          value={contributionType}
          onChange={(e) => onContributionTypeChange(e.target.value as ReceiptNature)}
          className="w-full rounded-md border px-3 py-2 text-sm"
        >
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {showMoreTypesButton && (
          <button
            type="button"
            onClick={onShowMoreTypes}
            className="mt-1 text-xs text-primary hover:underline"
          >
            More types
          </button>
        )}
      </div>

      {contributionType !== ReceiptNature.InKind && (
        <div>
          <label htmlFor="contrib-payment-method" className="block text-sm font-medium mb-1">
            How was it paid? *
          </label>
          <select
            id="contrib-payment-method"
            value={paymentMethod ?? ''}
            onChange={(e) =>
              onPaymentMethodChange(e.target.value ? (e.target.value as PaymentMethod) : null)
            }
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="">Select…</option>
            {MONETARY_PAYMENT_METHODS.map((pm) => (
              <option key={pm} value={pm}>
                {pm === PaymentMethod.Cash ? 'Cash (currency)' : paymentMethodLabel(pm)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* #711 — asked on the one receipt it decides something for: whether the transfer is exempt
          from the contribution limits and files on Schedule A line 12. */}
      {contributionType === ReceiptNature.FederalTransfer && (
        <TransferQualificationField
          qualification={transferQualification}
          onChange={onTransferQualificationChange}
          idPrefix="contrib-transfer-qualification"
        />
      )}

      {contributionType === ReceiptNature.InKind && (
        <div>
          <label htmlFor="contrib-inkind-description" className="block text-sm font-medium mb-1">Description *</label>
          <input
            id="contrib-inkind-description"
            type="text"
            value={inKindDescription}
            onChange={(e) => onInKindDescriptionChange(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            placeholder="e.g. Catering, office supplies, printing"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            What was given. This lands on the filing describing the in-kind contribution.
          </p>
        </div>
      )}

      {contributionType === ReceiptNature.InKind && (
        <div>
          <label htmlFor="contrib-fmv" className="block text-sm font-medium mb-1">Fair Market Value Basis *</label>
          <input
            id="contrib-fmv"
            type="text"
            value={inKindFairMarketValueBasis}
            onChange={(e) => onInKindFmvChange(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            placeholder="Description of in-kind value basis"
          />
        </div>
      )}

      {/* The in-kind nature is an Alabama-only classifier (required by AL EDI; every other
          state files free-text inKindDescription and never consumes the value). It renders
          exactly when required — `inKindNatureRequired` is the parent's jurisdiction gate —
          so off-AL the picker is hidden and no stored-but-never-filed value is invited. */}
      {contributionType === ReceiptNature.InKind && inKindNatureRequired && (
        <div>
          <label htmlFor="contrib-inkind-nature" className="block text-sm font-medium mb-1">In-kind nature *</label>
          <select
            id="contrib-inkind-nature"
            value={inKindNature ?? ''}
            onChange={(e) => onInKindNatureChange(e.target.value ? (e.target.value as InKindNature) : null)}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="">Select a nature...</option>
            {IN_KIND_NATURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {electionOptions.length > 0 && (
        <div>
          <label htmlFor="contrib-election" className="block text-sm font-medium mb-1">Election Designation</label>
          <select
            id="contrib-election"
            value={selectedElectionKey}
            onChange={(e) => {
              const opt = electionOptions.find((o) => o.key === e.target.value);
              if (opt) onElectionChange(opt.designation, opt.electionYear);
            }}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            {electionOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="contrib-notes" className="block text-sm font-medium mb-1">Notes</label>
        <textarea
          id="contrib-notes"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm"
          rows={2}
          placeholder="Optional notes"
        />
      </div>
    </>
  );
}
